import React from "react";
import { Typography } from "@mui/material";
import * as S from "./style";
import { useLanguage } from "../../../contexts/LanguageContext";
import { semantic, ramps } from "../../../common/theme/brandTokens";

interface VitalThreshold {
  flag_type: string;
  threshold_type: string;
  value: number;
}

interface LastReadingCardProps {
  label: string;
  value: number | null | undefined;
  unit: string;
  color: string;
  nextForecastValue?: number | null;
  vitalType?: string;
  thresholds?: VitalThreshold[];
}

function LastReadingCard({ 
  label, 
  value, 
  unit, 
  color, 
  nextForecastValue,
  vitalType,
  thresholds 
}: LastReadingCardProps) {
  const { t } = useLanguage();
  
  // Determine status based on thresholds
  let status: "normal" | "warning" | "critical" = "normal";
  let showIcon = false;
  let isHighViolation = false;
  
  if (thresholds && value != null && vitalType) {
    const vitalThresholds = thresholds.filter((t: VitalThreshold) => t);
    
    const criticalHigh = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "critical" && t.threshold_type === "high"
    )?.value;
    const criticalLow = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "critical" && t.threshold_type === "low"
    )?.value;
    const warningHigh = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "warning" && t.threshold_type === "high"
    )?.value;
    const warningLow = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "warning" && t.threshold_type === "low"
    )?.value;
    
    if (criticalHigh != null && value > criticalHigh) {
      status = "critical";
      showIcon = true;
      isHighViolation = true;
    } else if (criticalLow != null && value < criticalLow) {
      status = "critical";
      showIcon = true;
      isHighViolation = false;
    } else if (warningHigh != null && value > warningHigh) {
      status = "warning";
      showIcon = true;
      isHighViolation = true;
    } else if (warningLow != null && value < warningLow) {
      status = "warning";
      showIcon = true;
      isHighViolation = false;
    }
  }
  
  // Determine status color based on status (green/yellow/red)
  const statusColor = status === "critical" ? semantic.error.main : status === "warning" ? semantic.warning.main : semantic.success.main;
  
  // Title color: use status color for warning/critical, otherwise use vital color
  const titleColor = status !== "normal" ? statusColor : color;
  
  // Determine forecast status
  let forecastStatus: "normal" | "warning" | "critical" = "normal";
  let forecastColor: string = ramps.neutral[400]; // Default gray for text.disabled
  
  if (thresholds && nextForecastValue != null && vitalType) {
    const vitalThresholds = thresholds.filter((t: VitalThreshold) => t);
    
    const criticalHigh = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "critical" && t.threshold_type === "high"
    )?.value;
    const criticalLow = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "critical" && t.threshold_type === "low"
    )?.value;
    const warningHigh = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "warning" && t.threshold_type === "high"
    )?.value;
    const warningLow = vitalThresholds.find(
      (t: VitalThreshold) => t.flag_type === "warning" && t.threshold_type === "low"
    )?.value;
    
    if (criticalHigh != null && nextForecastValue > criticalHigh) {
      forecastStatus = "critical";
      forecastColor = semantic.error.main; // Red
    } else if (criticalLow != null && nextForecastValue < criticalLow) {
      forecastStatus = "critical";
      forecastColor = semantic.error.main; // Red
    } else if (warningHigh != null && nextForecastValue > warningHigh) {
      forecastStatus = "warning";
      forecastColor = semantic.warning.main; // Yellow
    } else if (warningLow != null && nextForecastValue < warningLow) {
      forecastStatus = "warning";
      forecastColor = semantic.warning.main; // Yellow
    } else {
      forecastStatus = "normal";
      forecastColor = semantic.success.main; // Green
    }
  }
  
  const trend =
    value != null && nextForecastValue != null
      ? Math.round(nextForecastValue) > Math.round(value)
        ? "up"
        : Math.round(nextForecastValue) < Math.round(value)
        ? "down"
        : "flat"
      : null;

  const trendArrow = trend === "up" ? "↗" : trend === "down" ? "↘" : "→";

  return (
    <S.CardRoot bordercolor={statusColor} status={status}>
      <S.CardLabel variant="caption" vitalcolor={titleColor}>
        {label}
      </S.CardLabel>
      <S.ValueRow vitalcolor={titleColor}>
        {value != null ? value.toFixed(0) : "—"}
        {value != null && <S.ValueUnit>{unit}</S.ValueUnit>}
      </S.ValueRow>
      {nextForecastValue != null && (
        <S.ForecastRow>
          <Typography
            variant="caption"
            sx={{ fontSize: 11, color: forecastColor, fontWeight: 600, lineHeight: 1 }}
          >
            {trendArrow} {nextForecastValue.toFixed(0)} {unit}
          </Typography>
        </S.ForecastRow>
      )}
      {showIcon && (
        <S.StatusIcon statuscolor={statusColor}>
          ! {isHighViolation ? t('vitals.high') : t('vitals.low')}
        </S.StatusIcon>
      )}
    </S.CardRoot>
  );
}

export default LastReadingCard;
